import { useTemplateQualityStore } from '@/store/templateQualityStore';
import { useTemplateStore } from '@/store/templateStore';

export interface TemplateQualitySummary {
  warnings: number;
  hints: number;
}

/**
 * Counts the unresolved quality findings of a template (WI #5667 / #5431), for
 * the release confirmation to show before the owner commits.
 *
 * Only the data: the dialog itself is a `UiModal` in the component, so it looks
 * like every other confirmation in the edit dialog.
 */
export function useTemplateQualitySummary() {
  /** `null` when the check could not be loaded — releasing must not be blocked. */
  async function loadQualitySummary(
    templateId: string,
  ): Promise<TemplateQualitySummary | null> {
    const templateStore = useTemplateStore();
    const qualityStore = useTemplateQualityStore();

    try {
      // Deterministic findings are computed in-browser from the loaded template,
      // so the full map has to be in the store before any count means anything.
      // The AI half is read from storage by the quality store's own load().
      if (!templateStore.hasTemplate(templateId)) {
        await templateStore.loadTemplate(templateId);
      }
      await qualityStore.load(templateId);
    } catch {
      return null;
    }

    const findings = qualityStore.allFindings;

    return {
      warnings: findings.filter((finding) => finding.severity === 'warning')
        .length,
      hints: findings.filter((finding) => finding.severity === 'info').length,
    };
  }

  return { loadQualitySummary };
}
